// 书源管理器实现

import type {
  BookSource,
  BookSourceValidationResult,
  BookSourceStatus
} from '@/types/reading';

import type { StorageManager } from './storage';
import { IndexedDBManager } from './indexedDBManager';

export class BookSourceManager {
  private storage: StorageManager;
  private validationCache: Map<string, BookSourceValidationResult> = new Map();

  constructor(storage?: StorageManager) {
    this.storage = storage || new IndexedDBManager();
  }

  // ============================================================================
  // 初始化
  // ============================================================================

  async initialize(): Promise<void> {
    await this.storage.initialize();
  }

  // ============================================================================
  // 书源验证
  // ============================================================================

  async validateBookSourceUrl(url: string): Promise<BookSourceValidationResult> {
    // 检查缓存
    if (this.validationCache.has(url)) {
      return this.validationCache.get(url)!;
    }

    const result: BookSourceValidationResult = {
      isValid: false,
      supportedFeatures: [],
      errors: [],
      warnings: []
    };

    try {
      // 1. URL格式验证
      if (!this.isValidUrl(url)) {
        result.errors.push('Invalid URL format');
        return result;
      }

      // 2. 获取书源JSON
      const bookSourceJson = await this.fetchBookSourceJson(url);
      if (!bookSourceJson) {
        result.errors.push('Failed to fetch book source JSON');
        return result;
      }

      // 3. 转换Legado格式
      const convertedJson = this.convertLegadoFormat(bookSourceJson);

      // 4. 验证转换后的JSON格式
      const validationResult = this.validateBookSourceJson(convertedJson);
      if (!validationResult.isValid) {
        result.errors.push(...validationResult.errors);
        result.warnings.push(...validationResult.warnings);
        return result;
      }

      // 5. 提取书源信息
      result.bookSourceName = convertedJson.bookSourceName;
      result.supportedFeatures = this.extractSupportedFeatures(convertedJson);
      result.isValid = true;
      result.warnings.push(...validationResult.warnings);

      // 缓存结果
      this.validationCache.set(url, result);

    } catch (error) {
      result.errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return result;
  }

  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private async fetchBookSourceJson(url: string): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X) AppleWebKit/605.1.15'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      return JSON.parse(text);

    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
      throw new Error('Unknown fetch error');
    }
  }

  private validateBookSourceJson(json: any): { isValid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 首先尝试转换Legado格式
    const convertedJson = this.convertLegadoFormat(json);

    // 检查必需字段
    const requiredFields = [
      'bookSourceName',
      'bookSourceUrl'
    ];

    // 基础字段检查
    for (const field of requiredFields) {
      const value = convertedJson[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`Missing required field: ${field}`);
      }
    }

    // 检查搜索规则 - 如果有搜索功能
    if (convertedJson.ruleSearch || convertedJson.searchUrl) {
      const searchRequiredFields = [
        'ruleSearchUrl',
        'ruleSearchList', 
        'ruleSearchName',
        'ruleSearchAuthor',
        'ruleSearchNoteUrl'
      ];

      for (const field of searchRequiredFields) {
        if (!convertedJson[field]) {
          warnings.push(`Missing search field: ${field} (search functionality may not work)`);
        }
      }
    }

    // 检查图书信息规则
    if (convertedJson.ruleBookInfo || convertedJson.bookInfoUrl) {
      const bookInfoRequiredFields = [
        'ruleBookName',
        'ruleBookAuthor',
        'ruleChapterList',
        'ruleChapterName',
        'ruleContentUrl'
      ];

      for (const field of bookInfoRequiredFields) {
        if (!convertedJson[field]) {
          warnings.push(`Missing book info field: ${field} (book details may not work)`);
        }
      }
    }

    // 检查内容规则
    if (!convertedJson.ruleBookContent) {
      warnings.push('Missing ruleBookContent (chapter content may not work)');
    }

    // 验证URL字段格式
    if (convertedJson.bookSourceUrl && !this.isValidUrl(convertedJson.bookSourceUrl)) {
      errors.push('Invalid bookSourceUrl format');
    }

    // 验证规则格式
    if (convertedJson.ruleSearchUrl && !convertedJson.ruleSearchUrl.includes('searchKey') && !convertedJson.ruleSearchUrl.includes('{{key}}')) {
      warnings.push('ruleSearchUrl should contain searchKey or {{key}} placeholder');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * 转换Legado格式的书源到内部格式
   */
  private convertLegadoFormat(json: any): any {
    // 如果已经是内部格式，直接返回
    if (json.ruleSearchUrl && json.ruleBookContent) {
      return json;
    }

    // 创建转换后的对象
    const converted: any = {
      bookSourceName: json.bookSourceName || json.sourceName || json.name || 'Unknown Source',
      bookSourceUrl: json.bookSourceUrl || json.sourceUrl || json.url || json.host || json.baseUrl || json.domain || '',
      bookSourceGroup: json.bookSourceGroup || json.sourceGroup || json.group || 'Default',
      enabled: json.enabled !== false,
      weight: json.weight || json.customOrder || json.order || 0,
      bookSourceType: json.bookSourceType || json.sourceType || 0,
      bookSourceComment: json.bookSourceComment || json.comment || json.description || '',
      bookUrlPattern: json.bookUrlPattern || json.urlPattern || '',
      enabledCookieJar: json.enabledCookieJar || json.cookieJar || false,
      enabledExplore: json.enabledExplore || json.explore || false,
      exploreUrl: json.exploreUrl || json.discoverUrl || '',
      checkUrl: json.checkUrl || json.testUrl || '',
      httpUserAgent: json.httpUserAgent || json.userAgent || (json.header && json.header['User-Agent']) || '',
      serialNumber: json.serialNumber || json.serial || 0,
      lastUpdateTime: json.lastUpdateTime || Date.now(),
      respondTime: json.respondTime || 0
    };

    // 如果还是没有URL，尝试从其他地方推断
    if (!converted.bookSourceUrl && json.bookSourceName) {
      console.warn(`No URL found for source "${json.bookSourceName}", using placeholder`);
      converted.bookSourceUrl = `https://example.com/${json.bookSourceName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`;
    }

    // 转换搜索规则 - 处理Legado的复杂结构
    if (json.searchUrl || json.ruleSearch) {
      // 搜索URL - 可能是字符串或JS代码
      converted.ruleSearchUrl = json.searchUrl || '';
      
      // 搜索规则
      if (json.ruleSearch) {
        converted.ruleSearchList = json.ruleSearch.bookList || '';
        converted.ruleSearchName = json.ruleSearch.name || '';
        converted.ruleSearchAuthor = json.ruleSearch.author || '';
        converted.ruleSearchCoverUrl = json.ruleSearch.coverUrl || '';
        converted.ruleSearchKind = json.ruleSearch.kind || '';
        converted.ruleSearchLastChapter = json.ruleSearch.lastChapter || '';
        converted.ruleSearchNoteUrl = json.ruleSearch.bookUrl || '';
        converted.ruleSearchWordCount = json.ruleSearch.wordCount || '';
        converted.ruleSearchIntro = json.ruleSearch.intro || '';
      }
    }

    // 转换图书信息规则
    if (json.ruleBookInfo) {
      converted.ruleBookName = json.ruleBookInfo.name || '';
      converted.ruleBookAuthor = json.ruleBookInfo.author || '';
      converted.ruleCoverUrl = json.ruleBookInfo.coverUrl || '';
      converted.ruleIntroduce = json.ruleBookInfo.intro || '';
      converted.ruleBookKind = json.ruleBookInfo.kind || '';
      converted.ruleBookLastChapter = json.ruleBookInfo.lastChapter || '';
      converted.ruleBookWordCount = json.ruleBookInfo.wordCount || '';
      converted.ruleBookTocUrl = json.ruleBookInfo.tocUrl || '';
    }

    // 转换章节目录规则
    if (json.ruleToc) {
      converted.ruleChapterList = json.ruleToc.chapterList || '';
      converted.ruleChapterName = json.ruleToc.chapterName || '';
      converted.ruleContentUrl = json.ruleToc.chapterUrl || '';
      converted.ruleChapterUrl = json.ruleToc.chapterUrl || '';
      converted.ruleChapterUrlNext = json.ruleToc.nextUrl || '';
      converted.ruleChapterIsVip = json.ruleToc.isVip || '';
      converted.ruleChapterUpdateTime = json.ruleToc.updateTime || '';
    }

    // 转换内容规则
    if (json.ruleContent) {
      converted.ruleBookContent = json.ruleContent.content || '';
      converted.ruleContentUrlNext = json.ruleContent.nextUrl || '';
    }

    // 转换发现规则
    if (json.ruleExplore) {
      converted.ruleExploreUrl = json.exploreUrl || '';
      converted.ruleExploreList = json.ruleExplore.bookList || '';
      converted.ruleExploreName = json.ruleExplore.name || '';
      converted.ruleExploreAuthor = json.ruleExplore.author || '';
      converted.ruleExploreCoverUrl = json.ruleExplore.coverUrl || '';
      converted.ruleExploreBookUrl = json.ruleExplore.bookUrl || '';
      converted.ruleExploreIntro = json.ruleExplore.intro || '';
      converted.ruleExploreKind = json.ruleExplore.kind || '';
      converted.ruleExploreLastChapter = json.ruleExplore.lastChapter || '';
    }

    // 如果是Legado格式但缺少某些字段，尝试从其他字段推断
    if (json.bookSourceType !== undefined) {
      // 这是Legado格式，但可能缺少规则字段
      // 只在完全缺失时设置默认值，保留原有的复杂规则
      if (!converted.ruleSearchUrl && converted.bookSourceUrl) {
        if (converted.bookSourceUrl.includes('qidian.com')) {
          converted.ruleSearchUrl = 'https://www.qidian.com/search?kw={{key}}';
        } else {
          converted.ruleSearchUrl = converted.bookSourceUrl + '/search?key={{key}}';
        }
      }
      
      // 只在完全缺失时设置默认值
      if (!converted.ruleSearchList) {
        converted.ruleSearchList = '.book-list .book-item';
      }
      if (!converted.ruleSearchName) {
        converted.ruleSearchName = '.book-title';
      }
      if (!converted.ruleSearchAuthor) {
        converted.ruleSearchAuthor = '.book-author';
      }
      if (!converted.ruleSearchNoteUrl) {
        converted.ruleSearchNoteUrl = '.book-link@href';
      }
      if (!converted.ruleBookName) {
        converted.ruleBookName = '.book-title, h1';
      }
      if (!converted.ruleBookAuthor) {
        converted.ruleBookAuthor = '.book-author';
      }
      if (!converted.ruleChapterList) {
        converted.ruleChapterList = '.chapter-list a';
      }
      if (!converted.ruleChapterName) {
        converted.ruleChapterName = '@text';
      }
      if (!converted.ruleContentUrl) {
        converted.ruleContentUrl = '@href';
      }
      if (!converted.ruleBookContent) {
        converted.ruleBookContent = '.chapter-content, #content';
      }
    }

    return converted;
  }

  private extractSupportedFeatures(json: any): string[] {
    const features: string[] = [];

    if (json.ruleSearchUrl) features.push('search');
    if (json.ruleCoverUrl || json.ruleSearchCoverUrl) features.push('covers');
    if (json.ruleIntroduce) features.push('descriptions');
    if (json.ruleChapterList) features.push('chapters');
    if (json.ruleBookContent) features.push('content');
    if (json.ruleFindUrl) features.push('discovery');
    if (json.checkUrl) features.push('health_check');

    return features;
  }

  // ============================================================================
  // 书源添加和管理
  // ============================================================================

  async addBookSource(url: string): Promise<BookSource[]> {
    // 1. 验证书源
    const validation = await this.validateBookSourceUrl(url);
    if (!validation.isValid) {
      throw new Error(`Invalid book source: ${validation.errors.join(', ')}`);
    }

    // 2. 获取书源JSON
    const json = await this.fetchBookSourceJson(url);
    
    // 3. 转换格式
    const sources = json.map((item: any) => this.convertLegadoFormat(item));
    
    const bookSources = await Promise.all(sources.map(async (convertedJson: any) => {
      // 4. 创建BookSource对象
      const bookSource: BookSource = {
        id: this.generateBookSourceId(convertedJson.bookSourceName, convertedJson.bookSourceUrl),
        bookSourceName: convertedJson.bookSourceName,
        bookSourceUrl: convertedJson.bookSourceUrl,
        bookSourceGroup: convertedJson.bookSourceGroup || 'Default',
        enabled: true,
        weight: convertedJson.weight || 0,
        ruleSearch: {
          ruleSearchUrl: convertedJson.ruleSearchUrl || '',
          ruleSearchList: convertedJson.ruleSearchList || '',
          ruleSearchName: convertedJson.ruleSearchName || '',
          ruleSearchAuthor: convertedJson.ruleSearchAuthor || '',
          ruleSearchCoverUrl: convertedJson.ruleSearchCoverUrl,
          ruleSearchKind: convertedJson.ruleSearchKind,
          ruleSearchLastChapter: convertedJson.ruleSearchLastChapter,
          ruleSearchNoteUrl: convertedJson.ruleSearchNoteUrl || ''
        },
        ruleBookInfo: {
          ruleBookName: convertedJson.ruleBookName || '',
          ruleBookAuthor: convertedJson.ruleBookAuthor || '',
          ruleCoverUrl: convertedJson.ruleCoverUrl,
          ruleIntroduce: convertedJson.ruleIntroduce,
          ruleChapterList: convertedJson.ruleChapterList || '',
          ruleChapterName: convertedJson.ruleChapterName || '',
          ruleContentUrl: convertedJson.ruleContentUrl || '',
          ruleChapterUrl: convertedJson.ruleChapterUrl,
          ruleChapterUrlNext: convertedJson.ruleChapterUrlNext
        },
        ruleContent: {
          ruleBookContent: convertedJson.ruleBookContent || '',
          ruleContentUrlNext: convertedJson.ruleContentUrlNext
        },
        lastUpdated: new Date(),
        status: 'active',
        checkUrl: convertedJson.checkUrl,
        httpUserAgent: convertedJson.httpUserAgent,
        serialNumber: convertedJson.serialNumber
      };

      // 5. 检查是否已存在
      const existing = await this.getBookSourceByUrl(convertedJson.bookSourceUrl);
      if (existing) {
        throw new Error('Book source already exists');
      }

      // 6. 保存到存储
      await this.storage.saveBookSource(bookSource);
      return bookSource;
    }))
  

    return bookSources;
  }

  async getBookSource(id: string): Promise<BookSource | null> {
    return await this.storage.getBookSource(id);
  }

  async getBookSourceByUrl(url: string): Promise<BookSource | null> {
    const sources = await this.storage.getBookSources();
    return sources.find(source => source.bookSourceUrl === url) || null;
  }

  async getAllBookSources(): Promise<BookSource[]> {
    return await this.storage.getBookSources();
  }

  async getEnabledBookSources(): Promise<BookSource[]> {
    const sources = await this.storage.getBookSources();
    return sources.filter(source => source.enabled);
  }

  async updateBookSource(id: string, updates: Partial<BookSource>): Promise<void> {
    const existing = await this.storage.getBookSource(id);
    if (!existing) {
      throw new Error('Book source not found');
    }

    const updated = {
      ...existing,
      ...updates,
      lastUpdated: new Date()
    };

    await this.storage.updateBookSource(id, updated);
  }

  async deleteBookSource(id: string): Promise<void> {
    await this.storage.deleteBookSource(id);
    
    // 清除相关缓存
    this.clearValidationCache();
  }

  // ============================================================================
  // 书源状态管理
  // ============================================================================

  async enableBookSource(id: string): Promise<void> {
    await this.updateBookSource(id, { enabled: true });
  }

  async disableBookSource(id: string): Promise<void> {
    await this.updateBookSource(id, { enabled: false });
  }

  async checkBookSourceStatus(id: string): Promise<BookSourceStatus> {
    const source = await this.storage.getBookSource(id);
    if (!source) {
      throw new Error('Book source not found');
    }

    try {
      // 如果有checkUrl，使用它进行健康检查
      if (source.checkUrl) {
        await this.fetchBookSourceJson(source.checkUrl);
      } else {
        // 否则使用主URL进行检查
        await this.fetchBookSourceJson(source.bookSourceUrl);
      }

      // 更新状态为active
      await this.updateBookSource(id, { status: 'active' });
      return 'active';

    } catch (error) {
      // 更新状态为error
      await this.updateBookSource(id, { status: 'error' });
      return 'error';
    }
  }

  async checkAllBookSourcesStatus(): Promise<Map<string, BookSourceStatus>> {
    const sources = await this.storage.getBookSources();
    const statusMap = new Map<string, BookSourceStatus>();

    const checkPromises = sources.map(async (source) => {
      try {
        const status = await this.checkBookSourceStatus(source.id);
        statusMap.set(source.id, status);
      } catch (error) {
        statusMap.set(source.id, 'error');
      }
    });

    await Promise.allSettled(checkPromises);
    return statusMap;
  }
  // ============================================================================
  // 导入导出
  // ============================================================================

  /**
   * 从URL导入大型书源文件（支持流式处理）
   */
  async importBookSourcesFromUrl(url: string, onProgress?: (progress: { loaded: number; total: number; sources: number }) => void): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      console.log('开始从URL导入书源:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X) AppleWebKit/605.1.15'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const totalSize = parseInt(response.headers.get('content-length') || '0');
      console.log(`文件大小: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);

      // 对于大文件，使用流式处理
      if (totalSize > 5 * 1024 * 1024) { // 5MB以上使用流式处理
        return await this.streamProcessLargeFile(response, totalSize, onProgress);
      } else {
        // 小文件直接读取
        const text = await response.text();
        return await this.importBookSources(text);
      }

    } catch (error) {
      console.error('URL导入失败:', error);
      result.errors.push(`导入失败: ${error instanceof Error ? error.message : '未知错误'}`);
      return result;
    }
  }

  /**
   * 流式处理大型书源文件
   */
  private async streamProcessLargeFile(
    response: Response, 
    totalSize: number, 
    onProgress?: (progress: { loaded: number; total: number; sources: number }) => void
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    let loadedBytes = 0;
    let buffer = '';
    let processedSources = 0;

    try {
      // 读取完整文件内容
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        loadedBytes += value.length;
        buffer += new TextDecoder().decode(value);

        // 报告进度
        if (onProgress) {
          onProgress({
            loaded: loadedBytes,
            total: totalSize,
            sources: processedSources
          });
        }
      }

      console.log('文件读取完成，开始解析JSON...');

      // 解析完整的JSON数组
      let sourcesArray: any[];
      try {
        const parsed = JSON.parse(buffer);
        // 确保解析结果是数组
        sourcesArray = Array.isArray(parsed) ? parsed : [parsed];
      } catch (parseError) {
        throw new Error(`JSON解析失败: ${parseError instanceof Error ? parseError.message : '未知错误'}`);
      }

      console.log(`解析到 ${sourcesArray.length} 个书源，开始批量处理...`);

      // 批量处理书源（每50个一批）
      const batchSize = 50;
      for (let i = 0; i < sourcesArray.length; i += batchSize) {
        const batch = sourcesArray.slice(i, i + batchSize);
        const batchResult = await this.processBatchSources(batch);
        
        result.success += batchResult.success;
        result.failed += batchResult.failed;
        result.errors.push(...batchResult.errors);
        
        processedSources = i + batch.length;

        // 报告处理进度
        if (onProgress) {
          onProgress({
            loaded: totalSize,
            total: totalSize,
            sources: processedSources
          });
        }

        // 避免阻塞UI，每批处理后稍作延迟
        if (i + batchSize < sourcesArray.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

    } finally {
      reader.releaseLock();
    }

    console.log(`导入完成: 成功 ${result.success}, 失败 ${result.failed}`);
    return result;
  }

  /**
   * 批量处理书源
   */
  private async processBatchSources(sources: any[]): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const sourceData of sources) {
      try {
        // 转换Legado格式
        const convertedData = this.convertLegadoFormat(sourceData);
        
        // 验证转换后的JSON格式
        const validation = this.validateBookSourceJson(convertedData);
        
        if (!validation.isValid) {
          result.failed++;
          result.errors.push(`${convertedData.bookSourceName || 'Unknown'}: ${validation.errors.join(', ')}`);
          continue;
        }

        // 创建BookSource对象
        const bookSource: BookSource = {
          id: this.generateBookSourceId(convertedData.bookSourceName, convertedData.bookSourceUrl),
          bookSourceName: convertedData.bookSourceName,
          bookSourceUrl: convertedData.bookSourceUrl,
          bookSourceGroup: convertedData.bookSourceGroup || 'Imported',
          enabled: convertedData.enabled !== false,
          weight: convertedData.weight || 0,
          ruleSearch: {
            ruleSearchUrl: convertedData.ruleSearchUrl || '',
            ruleSearchList: convertedData.ruleSearchList || '',
            ruleSearchName: convertedData.ruleSearchName || '',
            ruleSearchAuthor: convertedData.ruleSearchAuthor || '',
            ruleSearchCoverUrl: convertedData.ruleSearchCoverUrl,
            ruleSearchKind: convertedData.ruleSearchKind,
            ruleSearchLastChapter: convertedData.ruleSearchLastChapter,
            ruleSearchNoteUrl: convertedData.ruleSearchNoteUrl || ''
          },
          ruleBookInfo: {
            ruleBookName: convertedData.ruleBookName || '',
            ruleBookAuthor: convertedData.ruleBookAuthor || '',
            ruleCoverUrl: convertedData.ruleCoverUrl,
            ruleIntroduce: convertedData.ruleIntroduce,
            ruleChapterList: convertedData.ruleChapterList || '',
            ruleChapterName: convertedData.ruleChapterName || '',
            ruleContentUrl: convertedData.ruleContentUrl || '',
            ruleChapterUrl: convertedData.ruleChapterUrl,
            ruleChapterUrlNext: convertedData.ruleChapterUrlNext
          },
          ruleContent: {
            ruleBookContent: convertedData.ruleBookContent || '',
            ruleContentUrlNext: convertedData.ruleContentUrlNext
          },
          lastUpdated: new Date(),
          status: 'inactive',
          checkUrl: convertedData.checkUrl,
          httpUserAgent: convertedData.httpUserAgent,
          serialNumber: convertedData.serialNumber
        };

        // 检查是否已存在相同的书源
        const existing = await this.getBookSourceByUrl(convertedData.bookSourceUrl);
        if (existing) {
          // 更新现有书源
          await this.updateBookSource(existing.id, {
            ...bookSource,
            id: existing.id,
            lastUpdated: new Date()
          });
        } else {
          await this.storage.saveBookSource(bookSource);
        }
        
        result.success++;

      } catch (error) {
        result.failed++;
        result.errors.push(`${sourceData.bookSourceName || 'Unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return result;
  }
  /**
   * 从JSON字符串导入书源
   */
  async importBookSources(jsonData: string): Promise<{ success: number; failed: number; errors: string[] }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    try {
      const data = JSON.parse(jsonData);
      const sources = Array.isArray(data) ? data : [data];

      for (const sourceData of sources) {
        try {
          // 转换Legado格式
          const convertedData = this.convertLegadoFormat(sourceData);
          
          // 验证转换后的JSON格式
          const validation = this.validateBookSourceJson(convertedData);
          
          if (!validation.isValid) {
            result.failed++;
            result.errors.push(`${convertedData.bookSourceName || 'Unknown'}: ${validation.errors.join(', ')}`);
            continue;
          }

          // 创建BookSource对象
          const bookSource: BookSource = {
            id: this.generateBookSourceId(convertedData.bookSourceName, convertedData.bookSourceUrl),
            bookSourceName: convertedData.bookSourceName,
            bookSourceUrl: convertedData.bookSourceUrl,
            bookSourceGroup: convertedData.bookSourceGroup || 'Imported',
            enabled: convertedData.enabled !== false,
            weight: convertedData.weight || 0,
            ruleSearch: {
              ruleSearchUrl: convertedData.ruleSearchUrl || '',
              ruleSearchList: convertedData.ruleSearchList || '',
              ruleSearchName: convertedData.ruleSearchName || '',
              ruleSearchAuthor: convertedData.ruleSearchAuthor || '',
              ruleSearchCoverUrl: convertedData.ruleSearchCoverUrl,
              ruleSearchKind: convertedData.ruleSearchKind,
              ruleSearchLastChapter: convertedData.ruleSearchLastChapter,
              ruleSearchNoteUrl: convertedData.ruleSearchNoteUrl || ''
            },
            ruleBookInfo: {
              ruleBookName: convertedData.ruleBookName || '',
              ruleBookAuthor: convertedData.ruleBookAuthor || '',
              ruleCoverUrl: convertedData.ruleCoverUrl,
              ruleIntroduce: convertedData.ruleIntroduce,
              ruleChapterList: convertedData.ruleChapterList || '',
              ruleChapterName: convertedData.ruleChapterName || '',
              ruleContentUrl: convertedData.ruleContentUrl || '',
              ruleChapterUrl: convertedData.ruleChapterUrl,
              ruleChapterUrlNext: convertedData.ruleChapterUrlNext
            },
            ruleContent: {
              ruleBookContent: convertedData.ruleBookContent || '',
              ruleContentUrlNext: convertedData.ruleContentUrlNext
            },
            lastUpdated: new Date(),
            status: 'inactive',
            checkUrl: convertedData.checkUrl,
            httpUserAgent: convertedData.httpUserAgent,
            serialNumber: convertedData.serialNumber
          };

          // 检查是否已存在相同的书源
          const existing = await this.getBookSourceByUrl(convertedData.bookSourceUrl);
          if (existing) {
            // 更新现有书源而不是跳过
            await this.updateBookSource(existing.id, {
              ...bookSource,
              id: existing.id, // 保持原有ID
              lastUpdated: new Date()
            });
            result.success++;
          } else {
            await this.storage.saveBookSource(bookSource);
            result.success++;
          }

        } catch (error) {
          result.failed++;
          result.errors.push(`${sourceData.bookSourceName || 'Unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

    } catch (error) {
      result.failed++;
      result.errors.push(`JSON parse error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return result;
  }

  async exportBookSources(sourceIds?: string[]): Promise<string> {
    let sources: BookSource[];
    
    if (sourceIds && sourceIds.length > 0) {
      sources = [];
      for (const id of sourceIds) {
        const source = await this.storage.getBookSource(id);
        if (source) {
          sources.push(source);
        }
      }
    } else {
      sources = await this.storage.getBookSources();
    }

    const exportData = sources.map(source => ({
      bookSourceName: source.bookSourceName,
      bookSourceUrl: source.bookSourceUrl,
      bookSourceGroup: source.bookSourceGroup,
      enabled: source.enabled,
      weight: source.weight,
      checkUrl: source.checkUrl,
      httpUserAgent: source.httpUserAgent,
      serialNumber: source.serialNumber,
      
      // 搜索规则
      ruleSearchUrl: source.ruleSearch.ruleSearchUrl,
      ruleSearchList: source.ruleSearch.ruleSearchList,
      ruleSearchName: source.ruleSearch.ruleSearchName,
      ruleSearchAuthor: source.ruleSearch.ruleSearchAuthor,
      ruleSearchCoverUrl: source.ruleSearch.ruleSearchCoverUrl,
      ruleSearchKind: source.ruleSearch.ruleSearchKind,
      ruleSearchLastChapter: source.ruleSearch.ruleSearchLastChapter,
      ruleSearchNoteUrl: source.ruleSearch.ruleSearchNoteUrl,
      
      // 图书信息规则
      ruleBookName: source.ruleBookInfo.ruleBookName,
      ruleBookAuthor: source.ruleBookInfo.ruleBookAuthor,
      ruleCoverUrl: source.ruleBookInfo.ruleCoverUrl,
      ruleIntroduce: source.ruleBookInfo.ruleIntroduce,
      ruleChapterList: source.ruleBookInfo.ruleChapterList,
      ruleChapterName: source.ruleBookInfo.ruleChapterName,
      ruleContentUrl: source.ruleBookInfo.ruleContentUrl,
      ruleChapterUrl: source.ruleBookInfo.ruleChapterUrl,
      ruleChapterUrlNext: source.ruleBookInfo.ruleChapterUrlNext,
      
      // 内容规则
      ruleBookContent: source.ruleContent.ruleBookContent,
      ruleContentUrlNext: source.ruleContent.ruleContentUrlNext
    }));

    return JSON.stringify(exportData, null, 2);
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private generateBookSourceId(name: string, url: string): string {
    const timestamp = Date.now();
    const hash = this.simpleHash(name + url);
    return `source_${hash}_${timestamp}`;
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  private clearValidationCache(): void {
    this.validationCache.clear();
  }

  // ============================================================================
  // 统计信息
  // ============================================================================

  async saveBookSourceDirect(bookSource: BookSource): Promise<void> {
    await this.storage.saveBookSource(bookSource);
  }

  async getBookSourceStats(): Promise<{
    total: number;
    enabled: number;
    disabled: number;
    active: number;
    inactive: number;
    error: number;
    byGroup: Record<string, number>;
  }> {
    const sources = await this.storage.getBookSources();
    
    const stats = {
      total: sources.length,
      enabled: 0,
      disabled: 0,
      active: 0,
      inactive: 0,
      error: 0,
      byGroup: {} as Record<string, number>
    };

    for (const source of sources) {
      // 启用状态统计
      if (source.enabled) {
        stats.enabled++;
      } else {
        stats.disabled++;
      }

      // 运行状态统计
      switch (source.status) {
        case 'active':
          stats.active++;
          break;
        case 'inactive':
          stats.inactive++;
          break;
        case 'error':
          stats.error++;
          break;
      }

      // 分组统计
      const group = source.bookSourceGroup || 'Default';
      stats.byGroup[group] = (stats.byGroup[group] || 0) + 1;
    }

    return stats;
  }
}